import React from 'react';
import { Button } from '../components/ui/button';
import { BookOpen, Sparkles } from 'lucide-react';

const FALLBACK_COVER = 'https://images.squarespace-cdn.com/content/68247682061cc776f130e7ed/552ee632-8a04-43da-b258-118af34f36ae/9781918400045.png?content-type=image%2Fpng';

/**
 * Pure helper: from a list of books and an authors map, build the list of
 * series options (one per author) shown in the picker. A "series" here is
 * effectively the author — one author = one series.
 *
 * Each option has: { authorId, authorName, seriesName, coverUrl, bookCount }.
 *
 * `restrictToAuthorIds` is an optional Set: when provided, only authors whose
 * ID is in the set are included. Use this to show only "your" series (auto-
 * select case) vs all series (full picker case).
 */
export function buildSeriesOptions(books, authorsMap, restrictToAuthorIds = null) {
  const byAuthor = new Map();
  for (const b of books) {
    if (!b.authorId) continue;
    if (restrictToAuthorIds && !restrictToAuthorIds.has(b.authorId)) continue;
    if (!byAuthor.has(b.authorId)) {
      byAuthor.set(b.authorId, { books: [], cover: null, seriesName: '' });
    }
    const entry = byAuthor.get(b.authorId);
    entry.books.push(b);
    if (!entry.cover && b.coverUrl) entry.cover = b.coverUrl;
    if (!entry.seriesName && b.series) entry.seriesName = b.series;
  }

  const options = [];
  for (const [authorId, entry] of byAuthor.entries()) {
    const author = authorsMap?.[authorId] || {};
    options.push({
      authorId,
      authorName: author.penName || author.name || author.displayName || 'Author',
      seriesName: entry.seriesName || (author.penName || author.name || ''),
      coverUrl: entry.cover || FALLBACK_COVER,
      bookCount: entry.books.length,
    });
  }
  // Stable order: alphabetical by author name
  options.sort((a, b) => a.authorName.localeCompare(b.authorName));
  return options;
}

/**
 * Pure helper: collect the set of authorIds the reader has *some* relationship
 * with — i.e. has access to via approved campaign apps, booksReceived, or
 * approved arcRequests. Used to decide whether to auto-select a single series
 * or show the picker.
 */
export function getRelevantAuthorIds({ allBooks, myApprovedApps = [], readerData = null, arcRequests = [] }) {
  const ids = new Set();
  for (const a of myApprovedApps) {
    if (a.authorId) ids.add(a.authorId);
  }
  const received = new Set(readerData?.booksReceived || []);
  const approvedReqIds = new Set(
    arcRequests.filter(r => r.status === 'approved').map(r => r.bookId)
  );
  for (const b of allBooks) {
    if (received.has(b.id) || approvedReqIds.has(b.id)) {
      if (b.authorId) ids.add(b.authorId);
    }
  }
  return ids;
}

export default function SeriesPicker({
  options,
  onSelect,
  title = 'Choose your series',
  subtitle = 'Pick the series you\u2019d like to explore. You can switch any time.',
  embedded = false,
}) {
  const wrapperClass = embedded
    ? 'p-1'
    : 'min-h-screen bg-gradient-to-br from-slate-50/60 via-white to-slate-50/30 px-4 py-12 sm:py-20';

  return (
    <div className={wrapperClass}>
      <div className="mx-auto max-w-4xl">
        {!embedded && (
          <div className="mb-10 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-teal-100">
              <Sparkles className="h-7 w-7 text-teal-700" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 sm:text-4xl">{title}</h1>
            <p className="mt-3 text-base text-slate-600">{subtitle}</p>
          </div>
        )}
        {embedded && (
          <div className="mb-5">
            <p className="text-sm text-slate-600">{subtitle}</p>
          </div>
        )}

        {options.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white/60 p-10 text-center">
            <BookOpen className="mx-auto mb-3 h-12 w-12 text-slate-300" />
            <p className="text-slate-600 font-medium">No series available yet</p>
            <p className="mt-1 text-sm text-slate-400">Check back soon — new authors are joining all the time.</p>
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {options.map(opt => (
              <button
                key={opt.authorId}
                type="button"
                onClick={() => onSelect(opt.authorId)}
                className="group flex flex-col overflow-hidden rounded-2xl border-2 border-slate-100 bg-white text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-teal-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-teal-400"
              >
                <div className="relative h-44 bg-gradient-to-br from-slate-100 to-slate-200">
                  <img
                    src={opt.coverUrl}
                    alt={opt.seriesName || opt.authorName}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="font-bold text-slate-900 leading-tight">
                    {opt.seriesName || opt.authorName}
                  </h3>
                  <p className="mt-0.5 text-sm text-teal-700">by {opt.authorName}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    {opt.bookCount} {opt.bookCount === 1 ? 'book' : 'books'}
                  </p>
                  <Button className="mt-4 w-full bg-gradient-to-r from-slate-500 to-teal-600 py-2 text-sm font-semibold text-white hover:from-teal-600 hover:to-teal-700">
                    Enter series →
                  </Button>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
